// @spec SUP-STR-012
'use client';

import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { ArrowLeft } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { RiskDashboard } from '@/features/suppliers/components/RiskDashboard';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/_authenticated/suppliers/risk')({
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.supplierRiskDashboard'),
  }),
  component: SupplierRiskDashboardPage,
});

/**
 * Supplier Risk Dashboard page.
 */
function SupplierRiskDashboardPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const { organizations, isLoading: orgsLoading } = useOrganizations();

  // Get active organization
  const activeOrg = organizations.find((org) => org.isActive);

  // Show loading while checking organizations
  if (orgsLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-64 rounded bg-muted" />
            <div className="h-12 rounded bg-muted" />
            <div className="h-64 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  // No active organization selected
  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">{t('common:routeErrors.noOrgSelected')}</h2>
            <p className="mt-2 text-muted-foreground">
              {t('common:routeErrors.noOrgSelectedDesc')}
            </p>
            <Button
              variant="outline"
              onClick={() => void navigate({ to: '/settings/organizations' })}
              className="mt-4"
            >
              {t('common:routeErrors.goToOrganizations')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <header className="flex items-center gap-4">
          <Link to="/suppliers">
            <Button variant="ghost" size="icon">
              <ArrowLeft className="h-4 w-4" />
              <span className="sr-only">Back to suppliers</span>
            </Button>
          </Link>
          <div>
            <h1 className="text-3xl font-semibold text-foreground">Supplier Risk Dashboard</h1>
            <p className="mt-1 text-muted-foreground">
              Monitor and manage supplier risk across your organization
            </p>
          </div>
        </header>

        {/* Dashboard Content */}
        <RiskDashboard />
      </div>
    </div>
  );
}
